/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import type { OnboardingStepProps } from '../../registration';
import { useTheme } from '../../../../contexts/theme-context';
import {
  Heading,
  ContinueButton,
  ContentSection,
} from '../secure-your-account/secure-your-account.styled';
import { InputsContainer } from '../welcome/confirm-your-profile.styled';
import { VerificationCodeInput } from '../../../../components/verification-code-input/verification-code-input';
import {
  ActionButton,
  CountdownLabel,
  StatusLabel,
  PageBody,
  Prefix,
  Flag,
  PrefixText,
  CheckboxRow,
  CheckboxInput,
  CheckboxLabel,
  ActionsColumn,
  ConsentError,
  CodeGlowWrap,
  DropdownContainer,
  DropdownMenu,
  DropdownOption,
  OptionLeft,
  OptionFlag,
  OptionName,
  OptionCode,
} from './number-verification.styled';
import { FloatingInput } from '../../../../components/floating-input/floating-input';
import { sendCode, checkCode } from '../../../../helpers/sms-functions';
import { UniversalSpinner } from '../../../../components/universal-spinner/universal-spinner';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { VSpace } from '../../../../pages/chat/settings/account/account-settings';

/* ─────────────────────────────────────────────────────────────── */
/* Country Configuration                                            */
/* ─────────────────────────────────────────────────────────────── */

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  maxDigits: number;
}

const COUNTRIES: Country[] = [
  {
    code: 'US',
    name: 'United States',
    dialCode: '+1',
    flag: '/us-flag.png',
    maxDigits: 10,
  },
  {
    code: 'UK',
    name: 'United Kingdom',
    dialCode: '+44',
    flag: '/uk-flag.svg',
    maxDigits: 10,
  },
];

/* ─────────────────────────────────────────────────────────────── */
/* Phone helpers (updated for multi-country support)                */
/* ─────────────────────────────────────────────────────────────── */

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

// For storage/validation: get the appropriate number of digits for the country
const digitsForStorage = (raw: string, country: Country) => {
  const hasPlus = raw.trim().startsWith('+');
  const digits = digitsOnly(raw);
  const countryCodeDigits = country.dialCode.replace(/\D/g, '');

  // If pasted with country code, strip it
  if (hasPlus && digits.startsWith(countryCodeDigits)) {
    const withoutCode = digits.slice(countryCodeDigits.length);
    return withoutCode.slice(0, country.maxDigits);
  }

  // If has extra digits matching country code pattern, strip them
  if (
    digits.length > country.maxDigits &&
    digits.startsWith(countryCodeDigits)
  ) {
    return digits.slice(countryCodeDigits.length, countryCodeDigits.length + country.maxDigits);
  }

  return digits.slice(0, country.maxDigits);
};

// For display formatting
const digitsForDisplay = (raw: string, country: Country, preferStripCountryCode: boolean) => {
  const digits = digitsOnly(raw);
  const countryCodeDigits = country.dialCode.replace(/\D/g, '');

  if (
    preferStripCountryCode &&
    digits.length > country.maxDigits &&
    digits.startsWith(countryCodeDigits)
  ) {
    return digits.slice(countryCodeDigits.length, countryCodeDigits.length + country.maxDigits);
  }

  return digits.slice(0, country.maxDigits);
};

// Format phone for US
const formatUSPhone = (raw: string, preferStripCountryCode: boolean) => {
  const numbersOnly = digitsForDisplay(raw, COUNTRIES[0], preferStripCountryCode);
  const match = numbersOnly.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (!match) return '';

  const [, area, central, line] = match;

  let formatted = '';
  if (area) formatted = `(${area}`;
  if (area.length === 3) formatted += ') ';
  if (central) formatted += central;
  if (central.length === 3) formatted += '-';
  if (line) formatted += line;

  return formatted;
};

// Format phone for UK
const formatUKPhone = (raw: string, preferStripCountryCode: boolean) => {
  const numbersOnly = digitsForDisplay(raw, COUNTRIES[1], preferStripCountryCode);
  const match = numbersOnly.match(/^(\d{0,4})(\d{0,6})$/);
  if (!match) return '';

  const [, area, number] = match;

  let formatted = '';
  if (area) formatted = area;
  if (area.length === 4 && number) formatted += ' ';
  if (number) formatted += number;

  return formatted;
};

// Generic formatter that delegates to country-specific formatters
const formatPhone = (raw: string, country: Country, preferStripCountryCode: boolean) => {
  if (country.code === 'US') {
    return formatUSPhone(raw, preferStripCountryCode);
  } else if (country.code === 'UK') {
    return formatUKPhone(raw, preferStripCountryCode);
  }
  return digitsForDisplay(raw, country, preferStripCountryCode);
};

/* ─────────────────────────────────────────────────────────────── */

export const NumberVerificationStep: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { isLight } = useTheme();
  const { setRegistration, loading: onboardingLoading, user } = useAuth();

  /* country dropdown */
  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownClosing, setIsDropdownClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /* phone + consent */
  // Store the phone as the pretty formatted string (like ScheduleCallPage).
  const [phonePretty, setPhonePretty] = useState(() =>
    formatPhone(user?.phoneNumber ?? '', selectedCountry, true)
  );
  const [agreeSms, setAgreeSms] = useState(false);
  const [consentError, setConsentError] = useState('');

  // For validation + sending SMS, always derive digits from the pretty value.
  const phoneDigits = digitsForStorage(phonePretty, selectedCountry);
  const isPhoneValid = phoneDigits.length === selectedCountry.maxDigits;

  /* SMS / resend */
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /* verification code */
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'error' | 'success'>('idle');

  /* helpers */
  const canSend = isPhoneValid && !loading && agreeSms;
  const canResend = codeSent && secondsLeft === 0 && !loading;
  const canContinue = verifyStatus === 'success';
  const isContinueDisabled = !canContinue || verifying;

  /* ─── Dropdown Handlers ──────────────────────────────────── */

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      setIsDropdownClosing(true);
      setTimeout(() => {
        setIsDropdownOpen(false);
        setIsDropdownClosing(false);
      }, 200); // Match animation duration
    } else {
      setIsDropdownOpen(true);
    }
  };

  const selectCountry = (country: Country) => {
    setSelectedCountry(country);
    
    // Reformat the existing phone number for the new country
    const currentDigits = digitsForStorage(phonePretty, selectedCountry);
    const formatted = formatPhone(currentDigits, country, false);
    setPhonePretty(formatted);
    
    // Close dropdown with animation
    setIsDropdownClosing(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsDropdownClosing(false);
    }, 200);

    // Reset verification flow when country changes
    if (codeSent) {
      setCodeSent(false);
      setVerifyStatus('idle');
      setCode('');
      setVerifying(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setSecondsLeft(60);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isDropdownOpen
      ) {
        setIsDropdownClosing(true);
        setTimeout(() => {
          setIsDropdownOpen(false);
          setIsDropdownClosing(false);
        }, 200);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  /* ─── Handlers ───────────────────────────────────────────── */

  const resetVerificationFlow = () => {
    if (codeSent) {
      setCodeSent(false);
      setVerifyStatus('idle');
      setCode('');
      setVerifying(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setSecondsLeft(60);
    }
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text') || '';
    if (!pasted) return;

    // Prevent the default paste so onChange doesn't double-process
    e.preventDefault();

    // Extract digits, strip country code, format
    const formatted = formatPhone(pasted, selectedCountry, true);
    setPhonePretty(formatted);
    resetVerificationFlow();
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    const prevValue = phonePretty;

    const prevDigits = digitsOnly(prevValue);
    const nextDigits = digitsOnly(nextValue);

    const isDeleting = nextValue.length < prevValue.length;
    if (isDeleting) {
      // Let deletions behave naturally
      setPhonePretty(nextValue);
    } else {
      // If digit count jumps by more than 1, it's almost certainly paste/autofill
      const likelyPasteOrAutofill = nextDigits.length - prevDigits.length > 1;
      const formatted = formatPhone(nextValue, selectedCountry, likelyPasteOrAutofill);
      setPhonePretty(formatted);
    }

    resetVerificationFlow();
  };

  const handleContinue = () => {
    // Store phone number and consent
    setRegistration({ phoneNumber: `${selectedCountry.dialCode}${phoneDigits}`, smsConsent: agreeSms });
    goNext();
  };

  // Allow pasting a full 6-digit code to fill all inputs at once
  const handlePasteCode = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (!codeSent) return;
    const pasted = e.clipboardData.getData('text') || '';
    const digits = digitsOnly(pasted).slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    setCode(digits);
  };

  /* SEND / RESEND -------------------------------------------------- */
  const handleSendCode = async () => {
    if (!codeSent && !agreeSms) {
      setConsentError('Please agree to receive SMS updates to continue.');
      return;
    }
    if (!canSend && !canResend) return;

    setConsentError('');
    setLoading(true);

    // Send as E.164: +[countryCode][number]
    const fullNumber = `${selectedCountry.dialCode}${phoneDigits}`;
    const res = await sendCode(fullNumber);
    setLoading(false);

    if (!res.ok) {
      setConsentError(res.message);
      return;
    }

    /* success path */
    setCodeSent(true);
    setSecondsLeft(60);
    setVerifyStatus('idle');
    setCode('');
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((sec) => (sec <= 1 ? 0 : sec - 1));
    }, 1000);
  };

  /* AUTO-VERIFY ---------------------------------------------------- */
  useEffect(() => {
    // Once verified successfully, don't re-verify — Twilio codes are one-time use
    if (verifyStatus === 'success') return;

    // Reset verification if code is no longer complete
    if (!codeSent || code.length !== 6) {
      setVerifyStatus('idle');
      setVerifying(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setVerifying(true);
      const fullNumber = `${selectedCountry.dialCode}${phoneDigits}`;
      const res = await checkCode(fullNumber, code);
      if (cancelled) return;

      if (!res.ok) {
        setVerifyStatus('error');
        setConsentError(res.message);
      } else {
        setVerifyStatus(res.verified ? 'success' : 'error');
      }
      setVerifying(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [code, codeSent, phoneDigits, selectedCountry, verifyStatus]);

  /* AUTO-CONTINUE on successful verification */
  useEffect(() => {
    if (verifyStatus === 'success') {
      handleContinue();
    }
  }, [verifyStatus]);

  /* clear timer on unmount */
  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    },
    []
  );

  /* ─── JSX ─────────────────────────────────────────────────── */
  return (
    <ContentSection data-testid="ob-number">

      <div data-testid="ob-secure-headings">
        <VSpace $h={32} />
        <Heading isLight={isLight} data-testid="ob-secure-heading">
          Secure your <br />
          experience.{' '}
        </Heading>
      </div>

      <PageBody data-testid="ob-number-body">
        <InputsContainer data-testid="ob-number-inputs">
          <DropdownContainer ref={dropdownRef}>
            <FloatingInput
              id="phone"
              label="Phone Number"
              value={phonePretty}
              onChange={handlePhoneInput}
              onPaste={handlePhonePaste}
              required
              numericOnly
              autoComplete="tel"
              data-testid="ob-number-phone"
              prefix={
                <Prefix data-testid="ob-number-prefix" onClick={toggleDropdown}>
                  <img 
                    src="/phone-prefix-dropdown.svg" 
                    alt="Phone prefix dropdown" 
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  />
                  <Flag src={selectedCountry.flag} alt={`${selectedCountry.name} flag`} data-testid="ob-number-flag" />
                  <PrefixText data-testid="ob-number-prefix-text">{selectedCountry.dialCode}</PrefixText>
                </Prefix>
              }
              prefixWidth={selectedCountry.code === 'UK' ? 105 : 90}
            />
            
            <DropdownMenu isOpen={isDropdownOpen} isClosing={isDropdownClosing}>
              {COUNTRIES.map((country) => (
                <DropdownOption
                  key={country.code}
                  isSelected={selectedCountry.code === country.code}
                  onClick={() => selectCountry(country)}
                  data-testid={`ob-country-${country.code.toLowerCase()}`}
                >
                  <OptionLeft>
                    <OptionFlag src={country.flag} alt={`${country.name} flag`} />
                    <OptionName>{country.name}</OptionName>
                  </OptionLeft>
                  <OptionCode>{country.dialCode}</OptionCode>
                </DropdownOption>
              ))}
            </DropdownMenu>
          </DropdownContainer>

          {!codeSent && (
            <CheckboxRow data-testid="ob-number-consent-row">
              <CheckboxInput
                type="checkbox"
                id="sms-consent"
                checked={agreeSms}
                onChange={(e: any) => {
                  setAgreeSms(e.target.checked);
                  if (e.target.checked) setConsentError('');
                }}
                data-testid="ob-number-consent-input"
              />
              <CheckboxLabel htmlFor="sms-consent" data-testid="ob-number-consent-label">
                I agree to receive SMS updates from Mailander. Frequency varies. Msg &amp; Data
                rates may apply. Reply STOP to opt out. See our{' '}
                <a
                  style={{ color: '#5E84ED' }}
                  href="https://mailander.com/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ob-number-consent-terms"
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  style={{ color: '#5E84ED' }}
                  href="https://mailander.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ob-number-consent-privacy"
                >
                  Privacy policy
                </a>
                .
              </CheckboxLabel>
            </CheckboxRow>
          )}

          {consentError && (
            <ConsentError status="error" data-testid="ob-number-consent-error">
              {consentError}
            </ConsentError>
          )}

          {codeSent && (
            <CodeGlowWrap
              status={verifyStatus}
              data-testid="ob-number-code"
              onPaste={handlePasteCode}
            >
              <VerificationCodeInput
                length={6}
                value={code}
                onChange={setCode}
                status={verifyStatus}
              />
            </CodeGlowWrap>
          )}
        </InputsContainer>

        {codeSent && verifyStatus === 'success' && (
          <StatusLabel status="success" data-testid="ob-number-status-success">
            Number verified
          </StatusLabel>
        )}
        {codeSent && verifyStatus === 'error' && (
          <StatusLabel status="error" data-testid="ob-number-status-error">
            Verification failed
          </StatusLabel>
        )}

        {verifyStatus === 'idle' && codeSent && (
          <CountdownLabel data-testid="ob-number-countdown">
            {secondsLeft > 0
              ? `Didn't get the code? Re-send in 00:${secondsLeft.toString().padStart(2, '0')}`
              : `Didn't get it?`}
          </CountdownLabel>
        )}

        <ActionsColumn data-testid="ob-number-actions">
          <ActionButton
            disabled={(!codeSent && !canSend) || (codeSent && !canResend)}
            onClick={handleSendCode}
            data-testid="ob-number-send"
          >
            {loading && onboardingLoading ? (
              <UniversalSpinner />
            ) : codeSent ? (
              'Resend code'
            ) : (
              'Send code'
            )}
          </ActionButton>

          <ContinueButton
            type="button"
            disabled={isContinueDisabled}
            onClick={handleContinue}
            data-testid="ob-number-continue"
            css={{
              backgroundColor: isContinueDisabled ? '#2B2D33' : '#5E84ED',
            }}
          >
            Continue
          </ContinueButton>
        </ActionsColumn>
      </PageBody>
    </ContentSection>
  );
};

/* ───────── shell flags ───────── */
(NumberVerificationStep as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(NumberVerificationStep as any).fill = false;
(NumberVerificationStep as any).smallLogo = true;
(NumberVerificationStep as any).progress = 'Security';
